import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import CtaForm from '@/components/CtaForm';

export default function ApplyFree() {
  const [searchParams] = useSearchParams();
  const domain = searchParams.get('domain');
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="apple-card">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h1 className="text-3xl font-bold mb-4">Application Submitted!</h1>
              <p className="text-muted-foreground mb-6">
                Thanks for applying to use <strong>{domain}</strong>. I'll review your application
                and get back to you within 48 hours.
              </p>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  <strong>Next steps:</strong> Our team will evaluate your project against our
                  criteria and reach out with next steps or questions.
                </p>
                <Link to="/domains">
                  <Button variant="outline">Browse More Domains</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Link
            to={domain ? `/domain/${domain}` : '/domains'}
            className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {domain ? `Back to ${domain}` : 'Back to Domains'}
          </Link>
        </div>

        {/* Header */}
        <Card className="apple-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-3xl font-bold">Use {domain} for free</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <p className="text-muted-foreground">
                We sponsor promising, high-impact projects with premium domain usage. Limited slots
                available.
              </p>

              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Selection Criteria:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Clear use case and realistic business plan</li>
                  <li>• Demonstrated value to users or community</li>
                  <li>• Brand-safe, non-spam project</li>
                  <li>• Specific timeline and implementation details</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Application Form */}
        <Card className="apple-card">
          <CardHeader>
            <CardTitle>Application Details</CardTitle>
          </CardHeader>
          <CardContent>
            <CtaForm
              pageType="free"
              subjectPrefix="[FREE APPLY]"
              onSuccess={() => setSubmitted(true)}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
